// @flow
import i18n from "i18next";
import k from "src/i18n/keys";

import React, { useRef, useState, useCallback } from "react";
import { connect } from "react-redux";
import * as R from "ramda";

import {
  MessageText,
  ToogleText,
  AssistantTitle,
  MutedToogleText
} from "./styles";
import {
  AssistantIcon,
  ReminderPrompt
} from "src/components/messages/Prompt/styles";
import { ChatMessage, FlexSpacer } from "src/components/messages/Chat/styles";
import {
  selectMessage,
  deselectMessage,
  hideSelectOptions,
  showMobileOptions
} from "src/actions/chatmenu";
import WarningModal from "src/components/Modal/Warning";
import MessageCard from "src/components/messages/Chat/MessageNotch";

import Icon from "src/icons";
import ChatTimestamp from "src/components/messages/Chat/ChatTimestamp";
import MoreMenu from "src/components/messages/MoreMenu";
import SelectionIndicator from "src/components/messages/Chat/SelectIndicator";
import {
  getChatroomReminders,
  getChatroomMembership,
  getSelectedAction
} from "src/reducers";

import { setAttribute } from "src/actions/chatroom";
import MessageWithMentions from "src/components/messages/MessageWithMentions";
import useBoolean from "src/hooks/useBoolean";

import type { AppState, Message, UID, MessageID } from "src/types";

type Props = {
  message: Message,
  _setAttribute: Function,
  reminders: boolean,
  selectedAction: string,
  isSrwMobile: boolean,
  isMember: boolean,
  currentUser: UID,
  _selectMessage: Function,
  _deselectMessage: Function,
  _hideSelectOptions: Function,
  _showMobileOptions: Function,
  selectedMessages: Array<MessageID>
};

const ReminderUniBot = ({
  message,
  _setAttribute,
  reminders,
  selectedAction,
  isSrwMobile,
  currentUser,
  _selectMessage,
  _deselectMessage,
  _hideSelectOptions,
  _showMobileOptions,
  selectedMessages
}: Props) => {
  const [menu, setMenu] = useState(false);
  const { data, roomId, timestamp, delivered, success, author, id } = message;

  const {
    value: modal,
    setTrue: showModal,
    setFalse: hideModal
  } = useBoolean();
  const outerRef = useRef();

  const turnOffReminders = () => {
    _setAttribute(roomId, { reminders: false });
    hideModal();
  };

  // Render action menu when user is deleting or forwarding message(s)
  const showMenuActions = useCallback(() => {
    const currentActionFilter =
      selectedAction.length == 0 ||
      (selectedAction !== "DELETE" && selectedAction !== "FORWARD");

    if (currentActionFilter) setMenu(true);
    else setMenu(false);
  }, [menu, selectedAction]);

  const isOwner = currentUser === author;
  const isMessageSelected = selectedMessages.includes(id);

  const toggleMenu = useCallback(() => {
    if (selectedAction) {
      if (selectedAction === "MOBILE") {
        if (!isMessageSelected) {
          _hideSelectOptions();
          _showMobileOptions();
          _selectMessage(id, timestamp, isOwner);
        } else _deselectMessage(id);
      } else if (!isMessageSelected) _selectMessage(id, timestamp, isOwner);
      else _deselectMessage(id);
    }
  }, [selectedAction, id, timestamp, isOwner, isMessageSelected]);

  const handleMouseLeave = () => {
    setMenu(false);
  };

  return (
    <ReminderPrompt
      onMouseEnter={showMenuActions}
      onMouseLeave={handleMouseLeave}
      isSrwMobile={isSrwMobile}
      onClick={toggleMenu}
      first
      showSelect={selectedAction && selectedAction !== "MOBILE"}
    >
      {modal && (
        <WarningModal
          handleClose={hideModal}
          handleClick={turnOffReminders}
          heading="Turn off reminders"
          content="Are you sure you want to turn off reminders?"
          primaryText="Turn off"
          secondaryText="Cancel"
          outerRef={outerRef}
        />
      )}
      <ChatMessage id={id} showSelect={selectedAction}>
        <AssistantIcon>
          <Icon type="assistant" />
        </AssistantIcon>
        <MessageCard first maxWidth>
          <AssistantTitle>{i18n.t(k.UNIFIZE_ASSISTANT)}</AssistantTitle>
          <MessageText>
            <MessageWithMentions message={data || ""} />
          </MessageText>

          {R.isNil(reminders) || reminders ? (
            <ToogleText onClick={showModal}>
              {i18n.t(k.TURN_OFF_REMINDERS)}
            </ToogleText>
          ) : (
            <MutedToogleText>
              {i18n.t(k.REMINDERS_HAVE_BEEN_TURNED_OFF)}
            </MutedToogleText>
          )}

          <ChatTimestamp
            read={success}
            delivered={delivered}
            timestamp={timestamp}
          />
        </MessageCard>
        <FlexSpacer />
        {menu && !isSrwMobile ? (
          <MoreMenu
            id={id}
            timestamp={timestamp}
            isOwner={isOwner}
            author={author}
          />
        ) : null}
        <SelectionIndicator id={id} timestamp={timestamp} isOwner={isOwner} />
      </ChatMessage>
    </ReminderPrompt>
  );
};

const mapStateToProps = ({ app }: { app: AppState }, { message }) => ({
  reminders: getChatroomReminders(app, message.roomId),
  selectedAction: getSelectedAction(app),
  isSrwMobile: app.srw.isMobile,
  isMember: getChatroomMembership(app, app.chatRooms.current),
  currentUser: app.currentUser.uid,
  selectedMessages: app.chatRooms.selectedMessages
});

export default connect(mapStateToProps, {
  _setAttribute: setAttribute,
  _selectMessage: selectMessage,
  _deselectMessage: deselectMessage,
  _hideSelectOptions: hideSelectOptions,
  _showMobileOptions: showMobileOptions
})(ReminderUniBot);
